import {
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Typography,
    Box,
    Avatar,
    Chip,
    CircularProgress,
    Divider,
} from '@mui/material'
import {
    Close as CloseIcon,
    AttachFile as AttachFileIcon,
    ArticleOutlined as ArticleOutlinedIcon,
    DownloadOutlined as DownloadOutlinedIcon,
} from '@mui/icons-material'
import InfoIcon from '@mui/icons-material/Info';
import { useGetAnnouncementById } from 'services/api/announcements/announcementsHook'

const formatPostedAt = (dateStr) => {
  if (!dateStr) return ''

  const posted = new Date(dateStr)
  if (Number.isNaN(posted.getTime())) return ''

  const now = new Date()

  // Compare by local calendar day (midnight boundaries), not elapsed hours.
  const postedDay = new Date(posted.getFullYear(), posted.getMonth(), posted.getDate())
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())

  const diffDays = Math.round((today - postedDay) / (1000 * 60 * 60 * 24))

  if (diffDays === 0) return 'Posted today'
  if (diffDays === 1) return 'Posted 1 day ago'
  if (diffDays > 1 && diffDays <= 3) return `Posted ${diffDays} days ago`

  return `Posted at ${posted.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })}`
}

const AnnouncementDetailModal = ({ open, announcement, onClose }) => {
    const { data: detailData, isLoading } = useGetAnnouncementById(
        announcement?.id,
        { enabled: open && !!announcement?.id }
    )

    // Use fetched full detail; fall back to the list-item data while loading
    const data = detailData?.data || announcement
    const hasAttachments = Array.isArray(data?.attachments) && data.attachments.length > 0

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            keepMounted
            PaperProps={{
                sx: {
                    borderRadius: 2.5,
                },
            }}
        >
            {/* Header */}
            <DialogTitle
                sx={{
                    py: '15px',
                    px: '24px',
                }}
            >
                {/* Date chip */}
                <Chip
                    label={formatPostedAt(data?.posted_at || data?.published_at || data?.created_at)}
                    size="small"
                    sx={{
                        mb: 1,
                        fontSize: '12px',
                        height: 22,
                        // backgroundColor: '#F5F5F5',
                        color: 'text.secondary',
                        fontWeight: 400,
                        // borderRadius: '4px',
                    }}
                />

                {/* Title row */}
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, flex: 1 }}>
                        <InfoIcon sx={{ color: '#F3BF3A', fontSize: 27, mt: '2px', flexShrink: 0 }} />
                        <Box>
                            <Typography sx={{ fontWeight: 600, fontSize: '14px', color: 'text.primary', lineHeight: 1.4 }}>
                                {data?.title}
                            </Typography>
                            <Typography sx={{ fontSize: '12px', color: 'text.secondary', mt: '2px' }}>
                                Posted by {data?.author?.name || data?.author || data?.department || 'HR Department'}
                            </Typography>
                        </Box>
                    </Box>
                    <IconButton
                        onClick={onClose}
                        size="small"
                        sx={{ mt: '-4px', mr: '-8px', flexShrink: 0 }}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>
            </DialogTitle>

            <Divider />

            {/* Content */}
            <DialogContent sx={{ px: '24px', py: '15px' }}>
                {isLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress size={24} />
                    </Box>
                ) : (
                    <>
                        {/* Rich text content */}
                        <Box sx={{ position: 'relative' }}>
                            <Box sx={{ p: 2 }}>
                                {data?.body ? (
                                    <Box
                                        dangerouslySetInnerHTML={{ __html: data.body }}
                                        sx={{
                                            fontSize: '12px',
                                            lineHeight: 1.7,
                                            color: 'text.primary',
                                            '& p': { m: 0, mb: '8px' },
                                            '& p:last-child': { mb: 0 },
                                            '& ul': { pl: '20px', mb: '8px' },
                                            '& li': { mb: '4px' },
                                        }}
                                    />
                                ) : (
                                    <Typography sx={{ fontSize: '12px', lineHeight: 1.7, color: 'text.primary' }}>
                                        {data?.description}
                                    </Typography>
                                )}
                            </Box>
                        </Box>

                        <Divider />

                        {/* Attachments */}
                        {hasAttachments && (
                            <Box sx={{ px: '20px', py: '24px' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', mb: 2 }}>
                                    <AttachFileIcon sx={{ fontSize: 16, color: 'text.primary' }} />
                                    <Typography sx={{ fontSize: '12px', fontWeight: 400, color: 'text.primary' }}>
                                        Attachments ({data?.attachments?.length})
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    {data?.attachments?.map((file, index) => (
                                        <Box
                                            key={index}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '10px',
                                                px: 1.5,
                                                py: 1,
                                                border: '1px solid #E8E8E8',
                                                borderRadius: '6px',
                                                backgroundColor: '#FAFAFA',
                                            }}
                                        >
                                            <ArticleOutlinedIcon sx={{ fontSize: 20, color: '#2666BE', flexShrink: 0 }} />
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography
                                                    sx={{
                                                        fontSize: '13px',
                                                        fontWeight: 500,
                                                        color: 'text.primary',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    {file.file_name || file.name}
                                                </Typography>
                                                {(file.file_size || file.size) && (
                                                    <Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>
                                                        {file.file_size ? `${(file.file_size / 1024).toFixed(1)} KB` : file.size}
                                                    </Typography>
                                                )}
                                            </Box>
                                            <IconButton
                                                size="small"
                                                component="a"
                                                href={file.download_url || file.url || '#'}
                                                download={file.file_name || file.name}
                                                sx={{ color: 'text.secondary', flexShrink: 0 }}
                                            >
                                                <DownloadOutlinedIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        )}
                    </>
                )}
            </DialogContent>
        </Dialog>
    )
}

export default AnnouncementDetailModal
